import React, { FC, memo } from 'react';

import {
  Avatar,
  Box,
  Checkbox,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import { INotificationsData } from '@pharmaplan/common/models/NotificationsModel';
import AvatarFallback from '@pharmaplan/common/assets/icons/avatarPlaceholder.png';
import { ReactComponent as CalendarIcon } from '@pharmaplan/common/assets/icons/calendarIconNew.svg';
import { ReactComponent as Critical } from '@pharmaplan/common/assets/icons/notificationCriticalIcon.svg';
import {
  NotificationGroups,
  NotificationIdMap,
  NotificationPriorities,
  NotificationsMap,
  NotificationsType,
  PositiveIcons,
  ToUserTypings,
  TypeOfUser,
  getNotification,
  getTypeOfUser,
  logos,
  notificationActions,
  showConfirmFor,
  userPreferredTimeFormat,
} from '@pharmaplan/common';

import { ReactComponent as GreenTick } from '../../../../assets/svg/filledGreenTick.svg';
import { ReactComponent as Ticker } from '../../../../assets/svg/ticker.svg';
import { ReactComponent as NotAssignedIcon } from '../../../../assets/svg/notAssignedIcon.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/svg/deleteIcon.svg';
import { ReactComponent as UpdateIcon } from '../../../../assets/svg/updateIcon.svg';
import ThemeConstants from '../../../../theme/ThemeConstants';
import { getDayMonthDateYear, getHourMinute } from '../../../Reports/helpers';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import useNotification from '../../../../hooks/useNotification';
import { DeleteSelection } from '../../../../hooks/useNotificationDelete';
import { formatDate, timeString } from '../../../../helpers/Functions';
import strings from '../../../../localization';

import classes from './styles';
import getLongTermContractLabels from '../../../common/LongTermContractLabels/LongTermContractLabels';

interface INotificationItem {
  item: INotificationsData;
  closeNotification: () => void;
  selected: DeleteSelection;
  setSelected: React.Dispatch<React.SetStateAction<DeleteSelection>>;
  handleMarkAsRead: (id: string) => void;
}

type styleProperty = Record<string, SxProps<Theme> | string | number>;

interface INotificationContent {
  key: number;
  icon: React.JSX.Element | null;
  info: string | string[];
  infoStyle: styleProperty;
  containerStyle: Array<styleProperty> | styleProperty;
  iconStyle?: styleProperty;
}

const { pharmacist, pharmacy, technician, student } = TypeOfUser;
const { positive, update, delete: deleteGroup } = NotificationGroups;
const { tick, cancel } = PositiveIcons;

const NotificationItem: FC<INotificationItem> = ({
  item,
  selected,
  setSelected,
  closeNotification,
  handleMarkAsRead,
}) => {
  const dispatch = useAppDispatch();

  const allLogos = useAppSelector(logos);
  const userType = useAppSelector(getTypeOfUser);
  const timeFormat = useAppSelector(userPreferredTimeFormat);

  const { handleActions } = useNotification({
    handleMarkAsRead,
    closeNotification,
  });

  const { grey, eventChip, primary } = ThemeConstants;

  const {
    notificationId,
    profilePicture,
    text,
    notificationType,
    isVisited,
    confirmBefore,
    startDate,
    endDate,
    notificationDate,
    isContractShift,
    priority,
  } = item ?? {};

  const { iconType } = NotificationIdMap[notificationType] ?? {};
  const isBroadcast = notificationType === NotificationsType.BroadcastNotification;

  const isTick = iconType === tick;
  const isCancel = iconType === cancel;
  const isPharmacist = userType === TypeOfUser.pharmacist;
  const isPriority = priority === NotificationPriorities.critical;

  const getLogo = (event: INotificationsData) => {
    const { logoId: eventLogoId } = event;
    const { image } = allLogos.find(({ logoId }) =>
      logoId === eventLogoId) ?? {};
    return image;
  };

  const dateTime = `${getDayMonthDateYear(startDate)} | ${timeString(
    startDate,
    endDate,
    timeFormat,
  )}`;

  const confirmByDateTime = `${getDayMonthDateYear(
    confirmBefore ?? '',
  )} | ${getHourMinute(confirmBefore ?? '', timeFormat)} EST`;

  const userBasedLogos = ToUserTypings({
    [pharmacist]: getLogo(item),
    [technician]: getLogo(item),
    [student]: getLogo(item),
    [pharmacy]: profilePicture,
  });

  const logo = userBasedLogos[userType];

  const { selectedId } = selected;

  const handleDrawer = () => {
    handleActions(item);
  };

  const handleNegativeActionsDrawer = () => {
    handleMarkAsRead(notificationId);
    dispatch(getNotification(notificationId));
  };

  const positiveIconHandler = () => {
    if (isTick) {
      return <GreenTick fill={eventChip.requestedText} />;
    }

    if (isCancel) {
      return <NotAssignedIcon />;
    }

    return <GreenTick fill={primary.main} />;
  };

  const { RangeLabel } = getLongTermContractLabels({
    contractStartDate: startDate,
    contractEndDate: endDate,
    isLongTerm: isContractShift,
    successAction: notificationActions.getNotificationsList,
  });

  const handleGroups = () => {
    const notificationGroup = NotificationsMap[notificationType];
    switch (notificationGroup) {
      case positive:
        return {
          icon: positiveIconHandler(),
          action: handleDrawer,
        };

      case deleteGroup:
        return {
          icon: <DeleteIcon />,
          action: handleNegativeActionsDrawer,
        };

      case update:
        return {
          icon: <UpdateIcon />,
          action: handleDrawer,
        };

      default:
        return {
          icon: <NotAssignedIcon />,
          action: handleNegativeActionsDrawer,
        };
    }
  };

  const { icon: handledIcon, action: handledAction } = handleGroups();
  const includesConfirmBefore = showConfirmFor.includes(notificationType);

  const notificationContent: Array<INotificationContent> = [
    {
      key: 0,
      icon: handledIcon,
      info: text,
      infoStyle: classes.secondaryText,
      containerStyle: classes.secondaryContainer,
      iconStyle: classes.secondaryIconContainer,
    },
    (!isContractShift && !isBroadcast) && {
      key: 1,
      icon: <CalendarIcon fill={grey[500]} />,
      info: dateTime,
      infoStyle: isPriority ? classes.priorityDateText : classes.dateText,
      containerStyle: [classes.secondaryContainer, classes.alignCenter],
    },
  ].filter(Boolean) as Array<INotificationContent>;

  const confirmBeforeObject = {
    key: 2,
    icon: (
      <Box sx={classes.ticker}>
        <Ticker />
      </Box>
    ),
    info: strings.formatString(strings.confirmBy, confirmByDateTime) as string,
    infoStyle: classes.confirmText,
    containerStyle: [classes.confirmContainer],
  };

  if (includesConfirmBefore) {
    notificationContent.push(confirmBeforeObject);
  }

  const notificationContentMapper = () =>
    notificationContent.map((content) => {
      const { key, icon, info, infoStyle, containerStyle, iconStyle } = content ?? {};

      return (
        <Box key={key} sx={classes.itemContainer}>
          <Box sx={containerStyle}>
            <Box display="flex" sx={iconStyle}>
              {icon}
            </Box>
            <Typography sx={infoStyle}>{info}</Typography>
          </Box>
        </Box>
      );
    });

  const handleToggle = () => {
    if (selected.selectedId.includes(notificationId)) {
      setSelected((prev) =>
        ({
          ...prev,
          selectedId: prev.selectedId.filter(
            (inItem) =>
              inItem !== notificationId,
          ),
        }));
      return;
    }

    setSelected((prev) =>
      ({
        ...prev,
        selectedId: [...prev.selectedId, notificationId],
      }));
  };

  return (
    <Box
      sx={[
        classes.container,
        classes.shadowStyle,
        classes.contentContainer,
        isPriority ? classes.priority : {},
      ]}
      padding={2}
    >
      <Box sx={classes.checkbox}>
        <Checkbox
          edge="start"
          checked={selectedId.includes(notificationId)}
          onChange={handleToggle}
        />
      </Box>

      <Box sx={classes.contentContainer} onClick={handledAction}>
        <Avatar
          alt="Avatar"
          sx={classes.image}
          imgProps={{
            style: {
              ...(isPharmacist ? classes.imageContain : classes.imageCover),
              ...classes.image,
            },
          }}
          src={logo ?? AvatarFallback}
        />
        <Box sx={classes.contentContainer}>
          <Box sx={classes.content}>
            {notificationContentMapper()}
            <RangeLabel />
          </Box>

          <Box sx={classes.rightContainer}>
            <Box sx={classes.timestampContainer}>
              <Typography sx={classes.timestamp}>
                {formatDate(notificationDate, '{0} {1}', true)}
              </Typography>
              {!isVisited && <Box sx={classes.notVisited} />}
            </Box>

            <Box sx={{ alignSelf: 'flex-end' }}>
              {/* warning */}
              {isPriority && <Critical />}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default memo(NotificationItem);
